import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Button } from "@/components/ui/button";
import { Trophy, Medal, Printer, BookOpen, Users, Award, Download } from "lucide-react";
import { formatGradeWithScale } from "@/lib/grading";
import { computeStudentGrades } from "@/lib/rankingsCompute";

/**
 * Class Rankings — per-subject, per-period and grand-Final leaderboards.
 * Teachers pick a class + section; page fetches all students' grades in one
 * shot and computes rankings client-side using the same helpers as the
 * Report Card page (so numbers always match).
 *
 * Three view modes:
 *   1. "Overall" (default) — final-grade leaderboard (grand total).
 *   2. "Per Period"        — per-period leaderboard across all subjects.
 *   3. "Per Subject"       — per-category leaderboard, with an optional
 *                             period filter (All periods vs a specific term).
 */
export default function RankingsPage() {
  const { selectedSchool, selectedBranch } = useBranch();
  const branchId = selectedBranch?.id;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  // Special sentinel value that means "combine every section of this class"
  // — user asked for a Top-N leaderboard pooled across an entire class.
  const ALL_SECTIONS = "__all__";
  const isClassWide = sectionId === ALL_SECTIONS;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // View mode: "overall" | "period" | "subject"
  const [mode, setMode] = useState("overall");
  // Period filter used only in "subject" mode (empty = All periods)
  const [subjectPeriodId, setSubjectPeriodId] = useState("");
  // Which specific period leaderboard to show in "period" mode
  const [periodTabId, setPeriodTabId] = useState("");
  // Top-N filter (top 5, 10 or all)
  const [topN, setTopN] = useState(10);

  useEffect(() => {
    if (!branchId) { setClasses([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: branchId } });
        setClasses((data || []).filter((c) => c.class_type === "regular" || c.class_type === "modular"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  useEffect(() => {
    setSectionId("");
    if (!classId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: branchId, class_id: classId } });
        setSections(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [classId, branchId]);

  useEffect(() => {
    if (!sectionId || !branchId) { setData(null); return; }
    setLoading(true);
    (async () => {
      try {
        const params = isClassWide
          ? { branch_id: branchId, class_id: classId }
          : { branch_id: branchId, section_id: sectionId };
        const { data } = await api.get("/grading/rankings", { params });
        setData(data);
        if (data.periods?.length) setPeriodTabId(data.periods[0].id);
      } catch (e) { toast.error(formatApiError(e)); setData(null); }
      finally { setLoading(false); }
    })();
  }, [branchId, sectionId, classId, isClassWide]);

  // Class-wide mode currently only supports the Overall leaderboard —
  // per-period and per-subject views are not meaningful when pooling across
  // sections that may have different task setups. Auto-snap back to overall.
  useEffect(() => {
    if (isClassWide && mode !== "overall") setMode("overall");
  }, [isClassWide, mode]);
  // For each student, compute their grades using the shared helper.
  const studentRows = useMemo(() => {
    if (!data) return [];
    const scoresByStudent = new Map();
    const directsByStudent = new Map();
    for (const s of data.scores || []) {
      if (!scoresByStudent.has(s.student_id)) scoresByStudent.set(s.student_id, []);
      scoresByStudent.get(s.student_id).push(s);
    }
    for (const d of data.direct_grades || []) {
      if (!directsByStudent.has(d.student_id)) directsByStudent.set(d.student_id, []);
      directsByStudent.get(d.student_id).push(d);
    }
    return (data.students || []).map((stu) => {
      const g = computeStudentGrades({
        categories: data.categories,
        periods: data.periods,
        settings: data.settings,
        scale_ranges: data.scale_ranges,
        tasks: data.tasks,
        studentScores: scoresByStudent.get(stu.id) || [],
        studentDirects: directsByStudent.get(stu.id) || [],
      });
      return { student: stu, ...g };
    });
  }, [data]);

  // Rank the students for the currently-selected mode.
  const rankings = useMemo(() => {
    if (!studentRows.length) return [];
    let scored;
    if (mode === "overall") {
      scored = studentRows.map((r) => ({
        student: r.student,
        value: r.finalGrade,
        detail: null,
      }));
    } else if (mode === "period") {
      scored = studentRows.map((r) => {
        const pa = r.periodAvgs.find((p) => p.period.id === periodTabId);
        return { student: r.student, value: pa?.grade ?? null, detail: null };
      });
    } else {
      // Per-subject mode: rank each category separately below. For the
      // top-level "rankings" list we keep it empty; the render path uses
      // subjectRankings instead.
      return [];
    }
    return scored
      .filter((s) => s.value != null)
      .sort((a, b) => b.value - a.value)
      .map((s, idx) => ({ ...s, rank: idx + 1 }));
  }, [studentRows, mode, periodTabId]);

  // Per-subject rankings — one leaderboard per category.
  const subjectRankings = useMemo(() => {
    if (mode !== "subject" || !data?.categories?.length) return [];
    return data.categories.map((cat) => {
      const rows = studentRows.map((r) => {
        const cr = r.categoryRows.find((x) => x.id === cat.id);
        let value = null;
        if (!subjectPeriodId) {
          value = cr?.overall ?? null;
        } else {
          value = cr?.perPeriod.find((pp) => pp.period.id === subjectPeriodId)?.grade ?? null;
        }
        return { student: r.student, value };
      });
      const ranked = rows.filter((s) => s.value != null).sort((a, b) => b.value - a.value)
        .map((s, idx) => ({ ...s, rank: idx + 1 }));
      return { category: cat, ranked };
    });
  }, [studentRows, data, mode, subjectPeriodId]);

  const scale = data?.settings?.grading_scale_type || "percentage";
  const ranges = data?.scale_ranges || [];
  // On-screen display keeps the scale description (e.g. "A (Outstanding)"),
  // matches the Report Card page.
  const displayGrade = (v) => v == null ? "—" : formatGradeWithScale(v, scale, ranges);
  // Description-free variant for CSV export & print — teachers wanted a
  // cleaner leaderboard on paper and in Excel.
  const displayGradeShort = (v) => {
    if (v == null) return "—";
    const raw = formatGradeWithScale(v, scale, ranges);
    const idx = raw.indexOf(" (");
    return idx !== -1 && raw.endsWith(")") ? raw.slice(0, idx) : raw;
  };
  // Numerical percentage always appended (so letter-based scales still
  // show the underlying number). E.g. "A · 92.50%".
  const displayGradeWithPercent = (v) => {
    if (v == null) return "—";
    if (scale === "percentage") return `${v.toFixed(2)}%`;
    const short = displayGradeShort(v);
    return `${short} · ${v.toFixed(2)}%`;
  };
  // JSX variant used inside table cells. Description is hidden on print;
  // the numeric percentage always follows.
  const GradeCell = ({ value }) => {
    if (value == null) return <>—</>;
    if (scale === "percentage") return <>{value.toFixed(2)}%</>;
    const raw = formatGradeWithScale(value, scale, ranges);
    const idx = raw.indexOf(" (");
    const code = idx !== -1 && raw.endsWith(")") ? raw.slice(0, idx) : raw;
    const desc = idx !== -1 && raw.endsWith(")") ? raw.slice(idx) : "";
    return (
      <>
        {code}
        {desc && <span className="print:hidden">{desc}</span>}
        <span className="ml-1 text-brand-inkMuted font-normal">· {value.toFixed(2)}%</span>
      </>
    );
  };

  const className = classes.find((c) => c.id === classId)?.name || "";
  const sectionName = isClassWide
    ? "All Sections"
    : (sections.find((s) => s.id === sectionId)?.name || "");
  const currentPeriodName = data?.periods?.find((p) => p.id === periodTabId)?.period_name || "";
  const subjectPeriodName = data?.periods?.find((p) => p.id === subjectPeriodId)?.period_name || "All Periods";

  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadCsv = (rows, filename) => {
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    if (!data?.students?.length) return;
    const safeName = (s) => (s || "rankings").replace(/[^a-z0-9-_]+/gi, "_");
    const scope = `${safeName(className)}_${safeName(sectionName)}`;
    if (mode === "subject") {
      // One master file with every subject's leaderboard as rows.
      const rows = [
        [`Class Rankings — ${className || ""} · ${sectionName || ""}`],
        [`Period: ${subjectPeriodName}`],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
        ["Subject", "Rank", "Student", "Grade"],
      ];
      for (const sr of subjectRankings) {
        for (const r of sr.ranked.slice(0, topN)) {
          rows.push([sr.category.category_name, r.rank, r.student.full_name, displayGradeWithPercent(r.value)]);
        }
      }
      downloadCsv(rows, `rankings_${scope}_subjects.csv`);
      return;
    }
    const title =
      mode === "overall"
        ? (isClassWide ? "Grand Final Rankings — Whole Class" : "Grand Final Rankings")
        : `${currentPeriodName} Rankings`;
    const headerRow = isClassWide
      ? ["Rank", "Student", "Section", "Grade"]
      : ["Rank", "Student", "Grade"];
    const dataRows = rankings.slice(0, topN).map((r) =>
      isClassWide
        ? [r.rank, r.student.full_name, r.student.section_name || "", displayGradeWithPercent(r.value)]
        : [r.rank, r.student.full_name, displayGradeWithPercent(r.value)]
    );
    const rows = [
      [`Class Rankings — ${className || ""} · ${sectionName || ""}`],
      [title],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      headerRow,
      ...dataRows,
    ];
    downloadCsv(rows, `rankings_${scope}_${mode}.csv`);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="rankings-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 no-print">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
            <Trophy className="w-8 h-8 text-brand-purple" /> Class Rankings
          </h1>
          <p className="text-sm text-brand-inkMuted mt-1">
            Ranks every student in a section by subject, period, or grand Final Grade. Pick your class and section to begin.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportCsv}
            disabled={!studentRows.length}
            variant="outline"
            className="rounded-2xl"
            data-testid="rankings-export-csv"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export to Excel
          </Button>
          <Button
            onClick={() => window.print()}
            disabled={!rankings.length && !subjectRankings.length}
            className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
            data-testid="rankings-print-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>
      </header>

      {/* Print-only branded banner — replaces the on-screen filters when
          printing so the sheet reads like an official hallway leaderboard. */}
      {(rankings.length > 0 || subjectRankings.length > 0) && (
        <div className="print-only text-center border-b-2 border-foreground pb-3 mb-3" data-testid="rankings-print-banner">
          {selectedSchool?.logo_base64 && (
            <img src={selectedSchool.logo_base64} alt="" className="mx-auto mb-1 object-contain" style={{ height: "3rem", width: "3rem" }} />
          )}
          <p className="font-heading text-2xl font-bold text-foreground leading-tight">{selectedSchool?.name || "School"}</p>
          {selectedBranch?.name && (
            <p className="font-heading text-lg text-foreground/80 leading-tight">{selectedBranch.name}</p>
          )}
          <p className="font-heading text-2xl font-bold text-foreground mt-2 tracking-wide">CLASS RANKINGS</p>
          <p className="text-sm text-foreground/80 mt-0.5">
            {className}{sectionName ? ` — ${sectionName}` : ""}
            {mode === "overall" && " · Grand Final Grade"}
            {mode === "period" && currentPeriodName && ` · ${currentPeriodName}`}
            {mode === "subject" && ` · Per Subject · ${subjectPeriodName}`}
            {" · "}Top {topN >= 9999 ? "All" : topN}
          </p>
        </div>
      )}

      {/* Filters */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 no-print">
        <div className="grid sm:grid-cols-4 gap-3">
          <Field label="CLASS">
            <select value={classId} onChange={(e) => setClassId(e.target.value)}
              className="rankings-select" data-testid="rankings-class">
              <option value="">Select class</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="SECTION">
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)}
              disabled={!classId} className="rankings-select" data-testid="rankings-section">
              <option value="">Select section</option>
              {classId && (
                <option value={ALL_SECTIONS}>All Sections (Whole Class)</option>
              )}
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="TOP-N">
            <select value={topN} onChange={(e) => setTopN(Number(e.target.value))}
              className="rankings-select" data-testid="rankings-topn">
              <option value={5}>Top 5</option>
              <option value={10}>Top 10</option>
              <option value={20}>Top 20</option>
              <option value={9999}>Show all</option>
            </select>
          </Field>
          <Field label="VIEW">
            <div className="flex gap-1 rounded-2xl border border-brand-purple/20 p-1 bg-brand-purpleLight/20">
              {[
                { key: "overall", label: "Overall", icon: Award },
                { key: "period",  label: "Per Period", icon: BookOpen },
                { key: "subject", label: "Per Subject", icon: Users },
              ].map((opt) => {
                const Icon = opt.icon;
                // Class-wide only exposes Overall — other tabs render disabled
                // so the user still sees the option exists but can't confuse
                // themselves with a mixed-scale leaderboard.
                const disabled = isClassWide && opt.key !== "overall";
                return (
                  <button key={opt.key}
                    onClick={() => !disabled && setMode(opt.key)}
                    disabled={disabled}
                    title={disabled ? "Only available for a single section" : undefined}
                    className={`flex-1 rounded-xl px-2 py-1.5 text-xs font-heading font-semibold transition ${
                      mode === opt.key
                        ? "bg-brand-purple text-white shadow-sm"
                        : disabled
                          ? "text-brand-inkMuted/50 cursor-not-allowed"
                          : "text-foreground hover:bg-white"
                    }`}
                    data-testid={`rankings-mode-${opt.key}`}
                  >
                    <Icon className="w-3.5 h-3.5 inline mr-1" /> {opt.label}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        {/* Period tab bar (per-period mode) */}
        {mode === "period" && data?.periods?.length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap" data-testid="rankings-period-tabs">
            {data.periods.map((p) => (
              <button key={p.id}
                onClick={() => setPeriodTabId(p.id)}
                className={`rounded-full px-4 py-1.5 text-sm font-heading transition ${
                  periodTabId === p.id
                    ? "bg-brand-purple text-white shadow-sm"
                    : "bg-brand-purpleLight/40 text-foreground hover:bg-brand-purpleLight"
                }`}
                data-testid={`rankings-period-tab-${p.id}`}
              >
                {p.period_name}
              </button>
            ))}
          </div>
        )}

        {/* Period filter for per-subject mode */}
        {mode === "subject" && data?.periods?.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="rankings-subject-period-filter">
            <span className="text-xs font-heading font-semibold text-brand-inkMuted">FILTER BY PERIOD:</span>
            <button
              onClick={() => setSubjectPeriodId("")}
              className={`rounded-full px-3 py-1 text-sm font-heading transition ${
                subjectPeriodId === ""
                  ? "bg-brand-purple text-white"
                  : "bg-brand-purpleLight/40 text-foreground hover:bg-brand-purpleLight"
              }`}
            >
              All Periods (Avg)
            </button>
            {data.periods.map((p) => (
              <button key={p.id}
                onClick={() => setSubjectPeriodId(p.id)}
                className={`rounded-full px-3 py-1 text-sm font-heading transition ${
                  subjectPeriodId === p.id
                    ? "bg-brand-purple text-white"
                    : "bg-brand-purpleLight/40 text-foreground hover:bg-brand-purpleLight"
                }`}
              >
                {p.period_name}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Rankings body */}
      {loading ? (
        <p className="text-brand-inkMuted p-6">Loading rankings...</p>
      ) : !sectionId ? (
        <EmptyMsg title="Pick a class and section" subtitle="Choose the group of students you want to rank." />
      ) : !studentRows.length ? (
        <EmptyMsg title="No students in this section" subtitle="Add students to this section first." />
      ) : mode === "subject" ? (
        <div className="space-y-4">
          {subjectRankings.map((sr) => (
            <SubjectCard
              key={sr.category.id}
              category={sr.category}
              ranked={sr.ranked}
              topN={topN}
              GradeCell={GradeCell}
              subjectPeriodId={subjectPeriodId}
              periodName={data.periods.find((p) => p.id === subjectPeriodId)?.period_name}
            />
          ))}
        </div>
      ) : (
        <RankingsTable
          rankings={rankings.slice(0, topN)}
          GradeCell={GradeCell}
          showSection={isClassWide}
          title={
            mode === "overall"
              ? (isClassWide ? "Grand Final Rankings — Whole Class" : "Grand Final Rankings")
              : `Rankings — ${data.periods.find((p) => p.id === periodTabId)?.period_name || ""}`
          }
        />
      )}

      <style>{`
        .rankings-select {
          width: 100%; height: 2.75rem; padding: 0 0.75rem;
          border: 1px solid rgba(107, 33, 168, 0.2); border-radius: 0.75rem;
          background: white; font-family: inherit; font-size: 0.875rem;
        }
        .rankings-select:focus { outline: 2px solid rgba(107, 33, 168, 0.4); outline-offset: -1px; }
        .print-only { display: none; }
        @media print {
          @page { size: landscape; margin: 1cm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          aside, nav, header,
          [class*="sidebar" i], [class*="topbar" i],
          [data-testid="topbar-user-name"],
          [data-testid="navbar-logout-btn"],
          [data-testid="mobile-menu-btn"],
          .Toaster, [data-sonner-toaster] { display: none !important; }
          /* Force everything to be visible + printer-friendly */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          [data-testid="rankings-page"] { max-width: 100% !important; padding: 0 !important; }
          [data-testid="rankings-table"] { break-inside: avoid; }
          [data-testid^="rankings-subject-"] { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-heading font-semibold text-brand-inkMuted uppercase tracking-wider">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function EmptyMsg({ title, subtitle }) {
  return (
    <div className="text-center py-16 bg-white rounded-2xl border border-brand-purple/10 shadow-sm">
      <Trophy className="w-12 h-12 mx-auto text-brand-purple/30 mb-3" />
      <p className="font-heading text-lg font-semibold text-foreground">{title}</p>
      <p className="text-sm text-brand-inkMuted mt-1">{subtitle}</p>
    </div>
  );
}

/** Top-N leaderboard for Overall or a specific Period. */
function RankingsTable({ rankings, GradeCell, title, showSection }) {
  return (
    <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="rankings-table">
      <div className="px-4 py-3 border-b border-brand-purple/10 bg-brand-purpleLight/30">
        <h2 className="font-heading text-lg font-bold text-foreground">{title}</h2>
      </div>
      {rankings.length === 0 ? (
        <p className="p-6 text-center text-brand-inkMuted italic">No grades yet for this view.</p>
      ) : (
        <table className="w-full">
          <thead className="bg-brand-purple/5 text-xs uppercase tracking-wider text-brand-inkMuted">
            <tr>
              <th className="px-4 py-2 text-left w-16">Rank</th>
              <th className="px-4 py-2 text-left">Student</th>
              {showSection && <th className="px-4 py-2 text-left w-32">Section</th>}
              <th className="px-4 py-2 text-right w-32">Grade</th>
            </tr>
          </thead>
          <tbody>
            {rankings.map((r) => (
              <tr key={r.student.id} className="border-t border-brand-purple/5 hover:bg-brand-purpleLight/20" data-testid={`rankings-row-${r.rank}`}>
                <td className="px-4 py-2.5">
                  <RankBadge rank={r.rank} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    {r.student.photo
                      ? <img src={r.student.photo} alt="" className="w-8 h-8 rounded-full object-cover border border-brand-purple/20" />
                      : <div className="w-8 h-8 rounded-full bg-brand-purpleLight text-brand-purple flex items-center justify-center font-heading font-bold text-xs">
                          {(r.student.full_name || r.student.name || "?").charAt(0)}
                        </div>}
                    <span className="font-heading font-semibold text-foreground text-sm">
                      {r.student.full_name || `${r.student.name || ""} ${r.student.last_name || ""}`.trim() || "—"}
                    </span>
                  </div>
                </td>
                {showSection && (
                  <td className="px-4 py-2.5">
                    <span className="inline-block rounded-full bg-brand-purpleLight/60 text-brand-purple text-xs font-heading font-semibold px-2 py-0.5" data-testid={`rankings-section-badge-${r.rank}`}>
                      {r.student.section_name || "—"}
                    </span>
                  </td>
                )}
                <td className="px-4 py-2.5 text-right font-heading font-bold tabular-nums text-brand-purple">
                  <GradeCell value={r.value} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** One card per subject/category with its own Top-N leaderboard. */
function SubjectCard({ category, ranked, topN, GradeCell, periodName }) {
  const trimmed = ranked.slice(0, topN);
  return (
    <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid={`rankings-subject-${category.id}`}>
      <div className="px-4 py-2.5 border-b border-brand-purple/10 bg-brand-purpleLight/30">
        <p className="font-heading text-sm font-bold text-foreground truncate">{category.category_name}</p>
        <p className="text-[10px] uppercase tracking-wider text-brand-inkMuted">
          {periodName ? `${periodName} · Top ${Math.min(topN, ranked.length)}` : `All Periods (Avg) · Top ${Math.min(topN, ranked.length)}`}
        </p>
      </div>
      {trimmed.length === 0 ? (
        <p className="p-4 text-center text-xs text-brand-inkMuted italic">No grades yet.</p>
      ) : (
        <ul className="divide-y divide-brand-purple/5">
          {trimmed.map((r) => (
            <li key={r.student.id} className="px-3 py-2 flex items-center gap-2">
              <RankBadge rank={r.rank} small />
              <span className="text-sm text-foreground flex-1 truncate">
                {r.student.full_name || `${r.student.name || ""} ${r.student.last_name || ""}`.trim() || "—"}
              </span>
              <span className="font-heading font-bold tabular-nums text-brand-purple text-sm">
                <GradeCell value={r.value} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RankBadge({ rank, small }) {
  const size = small ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs";
  if (rank === 1) return (
    <span className={`${size} rounded-full bg-yellow-100 text-yellow-800 font-heading font-bold flex items-center justify-center border border-yellow-300`}>
      <Medal className={small ? "w-3 h-3" : "w-4 h-4"} />
    </span>
  );
  if (rank === 2) return (
    <span className={`${size} rounded-full bg-gray-100 text-gray-700 font-heading font-bold flex items-center justify-center border border-gray-300`}>
      2
    </span>
  );
  if (rank === 3) return (
    <span className={`${size} rounded-full bg-orange-100 text-orange-800 font-heading font-bold flex items-center justify-center border border-orange-300`}>
      3
    </span>
  );
  return (
    <span className={`${size} rounded-full bg-brand-purpleLight text-brand-purple font-heading font-bold flex items-center justify-center`}>
      {rank}
    </span>
  );
}
